import React from 'react';
import { Calculator, Utensils } from 'lucide-react';
import { useFoodContext } from '../context';
import { Button, Input } from '../components';
import { getCategoryColor, getCategoryEmoji } from '../utils/categories';
import { calculateGL } from '../utils/calculations';

/**
 * Calculator Page Component - Meal glycemic load calculator
 */
const CalculatorPage = () => {
  const {
    selectedFoods,
    updateFoodQuantity,
    toggleFoodSelection,
    totalMealGL,
    mealCategory,
    setActiveTab
  } = useFoodContext();

  if (selectedFoods.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-purple-600" />
            Calculateur de charge glycémique du repas
          </h2>

          <div className="text-center py-8">
            <Utensils className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              Aucun aliment sélectionné
            </h3>
            <p className="text-gray-500 mb-4">
              Allez dans l'onglet "Recherche" pour ajouter des aliments à votre repas
            </p>
            <Button
              onClick={() => setActiveTab('search')}
            >
              Rechercher des aliments
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-purple-600" />
          Calculateur de charge glycémique du repas
        </h2>

        {/* Selected Foods List */}
        <div className="space-y-4 mb-6">
          {selectedFoods.map(food => (
            <div key={food.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium text-gray-800">{food.name}</h4>
                {food.brand && (
                  <p className="text-sm text-gray-600">{food.brand}</p>
                )}
                <span className={`inline-block px-2 py-1 rounded text-xs mt-1 ${getCategoryColor(food.category)}`}>
                  {getCategoryEmoji(food.category)} {food.category}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={food.quantity || 100}
                  onChange={(e) => updateFoodQuantity(food.id, e.target.value)}
                  className="w-24 text-center"
                  min="0"
                  step="10"
                />
                <span className="text-gray-600 text-sm">g</span>
              </div>
              
              <div className="text-right min-w-[100px]">
                <div className="text-sm text-gray-600">IG: {food.gi}</div>
                <div className="text-sm text-gray-600">Glucides: {food.carbs}g</div>
                <div className="font-bold text-purple-600">
                  CG: {calculateGL(food.gi, food.carbs, food.quantity || 100)}
                </div>
              </div>
              
              <button
                onClick={() => toggleFoodSelection(food)}
                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                title="Retirer du repas"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* Total Calculation */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Charge Glycémique Totale du Repas
            </h3>
            <div className="text-4xl font-bold text-purple-600 mb-4">
              {totalMealGL}
            </div>
            <div className={`inline-block px-4 py-2 rounded-full font-medium ${getCategoryColor(mealCategory)}`}>
              {getCategoryEmoji(mealCategory)} Impact {mealCategory}
            </div>
          </div>
          
          {/* Recommendations */}
          <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">💡 Recommandations</h4>
            <div className="text-sm text-blue-700 space-y-1">
              {totalMealGL <= 5 && (
                <p>✅ Excellent ! Ce repas a un impact glycémique très faible.</p>
              )}
              {totalMealGL > 5 && totalMealGL <= 10 && (
                <p>👍 Bon choix ! Ce repas a un impact glycémique faible.</p>
              )}
              {totalMealGL > 10 && totalMealGL <= 20 && (
                <p>⚠️ Impact modéré. Vous pourriez réduire les portions ou ajouter des légumes.</p>
              )}
              {totalMealGL > 20 && (
                <p>🔴 Impact élevé. Considérez réduire les portions d'aliments à IG élevé.</p>
              )}
              
              <p className="mt-2 text-xs">
                <strong>Astuce :</strong> Ajoutez des fibres (légumes verts) et des protéines pour réduire l'impact glycémique global.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex justify-center gap-4 mt-6">
          <Button
            onClick={() => setActiveTab('search')}
            variant="outline"
          >
            Ajouter d'autres aliments
          </Button>
          
          <Button
            onClick={() => {
              selectedFoods.forEach(food => toggleFoodSelection(food));
            }}
            variant="secondary"
          >
            Vider le repas
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;